import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Save, 
  X, 
  Plus,
  Trash2,
  Send,
  CheckCircle,
  User,
  Mail,
  Phone,
  AlertCircle,
  FileText,
  Calendar,
  Settings,
  Bell,
  GripVertical,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import ContractService from '../../services/contractService';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import FormStepper from '../common/FormStepper';
import ConfirmationModal from '../common/ConfirmationModal';

const ESignatureEdit = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [document, setDocument] = useState(null);

  const STEPS = [
    { id: 0, label: 'Document Details', icon: FileText },
    { id: 1, label: 'Recipients', icon: User },
    { id: 2, label: 'Signature Settings', icon: Settings },
    { id: 3, label: 'Signature Progress', icon: CheckCircle }
  ];

  // Form state
  const [formData, setFormData] = useState({
    documentName: '',
    documentType: 'Contract',
    contractId: '',
    priority: 'Medium',
    dueDate: '',
    signatureMethod: 'Electronic Signature',
    authenticationMethod: 'Email Verification',
    reminderEnabled: true,
    reminderFrequency: 'daily',
    maxReminders: 3,
    notes: '',
    recipients: []
  });

  // Load document data
  useEffect(() => {
    loadDocumentData();
  }, [documentId]);

  const loadDocumentData = async () => {
    setLoading(true);
    try {
      // Fetch contract data and transform to e-signature format
      const response = await ContractService.getContractById(documentId);
      const contract = response.data;
      
      // Transform contract to e-signature document
      const signatureDoc = {
        id: contract.contractId || contract.id,
        documentName: contract.title || contract.name || 'Untitled Contract',
        documentType: contract.type || 'Contract',
        contractId: contract.contractId || contract.id,
        priority: contract.priority || 'Medium',
        dueDate: contract.endDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        signatureMethod: 'Electronic Signature',
        authenticationMethod: 'Email Verification',
        reminderEnabled: true,
        reminderFrequency: 'daily',
        maxReminders: 3,
        notes: contract.description || '',
        recipients: [
          {
            id: `recipient-${Date.now()}`,
            name: contract.counterparty || 'Unknown',
            email: contract.counterpartyContact?.email || '',
            phone: contract.counterpartyContact?.phone || '',
            role: 'Signer',
            order: 1,
            status: contract.status === 'EXECUTED' ? 'Completed' : 'Pending',
            signedAt: contract.status === 'EXECUTED' ? contract.updatedAt : null,
            authenticationMethod: 'Email Verification'
          }
        ],
        status: contract.status === 'EXECUTED' ? 'Completed' : 
                contract.status === 'IN_REVIEW' ? 'In Progress' : 'Pending Signature',
        createdDate: contract.createdAt || new Date().toISOString(),
        lastActivity: contract.updatedAt || contract.createdAt || new Date().toISOString(),
        completionPercentage: contract.status === 'EXECUTED' ? 100 : 
                              contract.status === 'IN_REVIEW' ? 50 : 0
      };

      setDocument(signatureDoc);
      setFormData({
        documentName: signatureDoc.documentName,
        documentType: signatureDoc.documentType,
        contractId: signatureDoc.contractId,
        priority: signatureDoc.priority,
        dueDate: signatureDoc.dueDate,
        signatureMethod: signatureDoc.signatureMethod,
        authenticationMethod: signatureDoc.authenticationMethod,
        reminderEnabled: signatureDoc.reminderEnabled,
        reminderFrequency: signatureDoc.reminderFrequency,
        maxReminders: signatureDoc.maxReminders,
        notes: signatureDoc.notes,
        recipients: signatureDoc.recipients
      });
    } catch (error) {
      console.error('Error loading document:', error);
      showError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Recipient management
  const handleAddRecipient = () => {
    const newRecipient = {
      id: `recipient-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: '',
      email: '',
      phone: '',
      role: 'Signer',
      order: formData.recipients.length + 1,
      status: 'Pending',
      signedAt: null,
      authenticationMethod: 'Email Verification'
    };
    setFormData(prev => ({
      ...prev,
      recipients: [...prev.recipients, newRecipient]
    }));
  };

  const handleRemoveRecipient = (recipientId) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients
        .filter(r => r.id !== recipientId)
        .map((r, index) => ({ ...r, order: index + 1 }))
    }));
  };

  const handleRecipientChange = (recipientId, field, value) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.map(r => 
        r.id === recipientId ? { ...r, [field]: value } : r
      )
    }));
  };

  const handleMoveRecipient = (recipientId, direction) => {
    setFormData(prev => {
      const recipients = [...prev.recipients];
      const index = recipients.findIndex(r => r.id === recipientId);
      if (index === -1) return prev;
      
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= recipients.length) return prev;
      
      [recipients[index], recipients[newIndex]] = [recipients[newIndex], recipients[index]];
      recipients.forEach((r, i) => { r.order = i + 1; });
      
      return { ...prev, recipients };
    });
  };

  // Save document
  const handleSave = async () => {
    setSaving(true);
    try {
      // Validate required fields
      if (!formData.documentName.trim()) {
        showError('Document name is required');
        return;
      }
      if (formData.recipients.length === 0) {
        showError('At least one recipient is required');
        return;
      }
      if (formData.recipients.some(r => !r.name || !r.email)) {
        showError('All recipients must have a name and email');
        return;
      }

      // Update contract with e-signature data
      const contractUpdate = {
        title: formData.documentName,
        type: formData.documentType,
        priority: formData.priority,
        endDate: formData.dueDate,
        description: formData.notes,
        eSignature: {
          signatureMethod: formData.signatureMethod,
          authenticationMethod: formData.authenticationMethod,
          reminderEnabled: formData.reminderEnabled,
          reminderFrequency: formData.reminderFrequency,
          maxReminders: formData.maxReminders,
          recipients: formData.recipients
        }
      };

      await ContractService.updateContract(documentId, contractUpdate);
      success('Document saved successfully');
      await loadDocumentData();
    } catch (error) {
      console.error('Error saving document:', error);
      showError('Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  // Send for signature
  const handleSendForSignature = async () => {
    setSending(true);
    try {
      // Validate recipients
      if (formData.recipients.length === 0) {
        showError('At least one recipient is required');
        return;
      }

      // Simulate sending for signature
      // In a real implementation, this would call an e-signature service API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      success('Document sent for signature successfully');
      setShowSendModal(false);
      await loadDocumentData();
    } catch (error) {
      console.error('Error sending document:', error);
      showError('Failed to send document for signature');
    } finally {
      setSending(false);
    }
  };

  // Send reminder
  const handleSendReminder = async (recipientId) => {
    try {
      const recipient = formData.recipients.find(r => r.id === recipientId);
      if (!recipient) return;

      // Simulate sending reminder
      await new Promise(resolve => setTimeout(resolve, 500));
      success(`Reminder sent to ${recipient.name || recipient.email}`);
    } catch (error) {
      console.error('Error sending reminder:', error);
      showError('Failed to send reminder');
    }
  };

  // Delete document
  const handleDelete = async () => {
    try {
      await ContractService.deleteContract(documentId);
      success('Document deleted successfully');
      navigate('/contracts/e-signature');
    } catch (error) {
      console.error('Error deleting document:', error);
      showError('Failed to delete document');
    }
  };

  // Navigation functions
  const handleNext = (e) => {
    if (e) e.preventDefault();
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(s => s + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleStepClick = (idx) => {
    if (idx <= currentStep) setCurrentStep(idx);
  };

  const handleCancel = () => {
    navigate('/contracts/e-signature');
  };

  const headerActions = [
    {
      label: 'Cancel',
      variant: 'outline',
      onClick: handleCancel,
      disabled: saving || sending
    },
    {
      label: saving ? 'Saving...' : 'Save',
      icon: <Save size={18} />,
      variant: 'primary',
      onClick: handleSave,
      disabled: saving || sending
    }
  ];

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="E-Signature Document"
          description="Loading document details..."
          actions={[]}
        />
        <div style={{ padding: '24px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            flexDirection: 'column',
            gap: '16px',
            backgroundColor: 'white',
            border: '1px solid var(--border-color)',
            padding: '40px',
          }}>
            <div className="loading-spinner"></div>
            <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-family)' }}>
              Loading document...
            </p>
          </div>
        </div>
      </>
    );
  }

  // Step renderers
  const renderStep0 = () => (
    <div>
      <h3 style={{ 
        marginBottom: '24px', 
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-family)',
        fontSize: '18px',
        fontWeight: '600'
      }}>
        Document Information
      </h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Document Name *
          </label>
          <input
            type="text"
            value={formData.documentName}
            onChange={(e) => handleChange('documentName', e.target.value)}
            className="form-control"
            placeholder="Enter document name"
          />
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Document Type
          </label>
          <select
            value={formData.documentType}
            onChange={(e) => handleChange('documentType', e.target.value)}
            className="form-control"
          >
            <option value="Contract">Contract</option>
            <option value="Employment Contract">Employment Contract</option>
            <option value="Vendor Contract">Vendor Contract</option>
            <option value="NDA">NDA</option>
            <option value="License Agreement">License Agreement</option>
            <option value="Lease Agreement">Lease Agreement</option>
          </select>
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Contract ID
          </label>
          <input
            type="text"
            value={formData.contractId}
            readOnly
            className="form-control"
            style={{ backgroundColor: '#f3f4f6', color: 'var(--text-secondary)' }}
          />
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Priority
          </label>
          <select
            value={formData.priority}
            onChange={(e) => handleChange('priority', e.target.value)}
            className="form-control"
          >
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Due Date *
          </label>
          <input
            type="date"
            value={formData.dueDate}
            onChange={(e) => handleChange('dueDate', e.target.value)}
            className="form-control"
          />
        </div>
      </div>

      <div>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          color: 'var(--text-primary)',
          fontFamily: 'var(--font-family)',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          Notes
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={4}
          className="form-control"
          placeholder="Add any additional notes or instructions..."
          style={{ resize: 'vertical' }}
        />
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ 
          margin: 0, 
          color: 'var(--text-primary)',
          fontFamily: 'var(--font-family)',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          Recipients ({formData.recipients.length})
        </h3>
        <button
          type="button"
          onClick={handleAddRecipient}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: '#4678be',
            color: 'white',
            border: 'none',
            borderRadius: '0',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)'
          }}
        >
          <Plus size={16} />
          Add Recipient
        </button>
      </div>

      {formData.recipients.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--text-secondary)',
          fontFamily: 'var(--font-family)',
          border: '1px solid var(--border-color)',
          borderRadius: '0',
          backgroundColor: '#f9fafb'
        }}>
          <User size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p>No recipients added. Click "Add Recipient" to get started.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {formData.recipients.map((recipient, index) => (
            <div
              key={recipient.id}
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: '0',
                padding: '20px',
                backgroundColor: 'white'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <GripVertical size={20} style={{ color: 'var(--text-secondary)', cursor: 'move' }} />
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#4678be',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)'
                  }}>
                    {recipient.order}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => handleMoveRecipient(recipient.id, 'up')}
                      style={{
                        padding: '6px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '0',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                      title="Move up"
                    >
                      ↑
                    </button>
                  )}
                  {index < formData.recipients.length - 1 && (
                    <button
                      type="button"
                      onClick={() => handleMoveRecipient(recipient.id, 'down')}
                      style={{
                        padding: '6px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '0',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                      title="Move down"
                    >
                      ↓
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveRecipient(recipient.id)}
                    style={{
                      padding: '6px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      color: '#EF4444'
                    }}
                    title="Remove recipient"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'var(--text-secondary)',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={recipient.name}
                    onChange={(e) => handleRecipientChange(recipient.id, 'name', e.target.value)}
                    className="form-control"
                    placeholder="Recipient name"
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'var(--text-secondary)',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={recipient.email}
                    onChange={(e) => handleRecipientChange(recipient.id, 'email', e.target.value)}
                    className="form-control"
                    placeholder="recipient@example.com"
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'var(--text-secondary)',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={recipient.phone}
                    onChange={(e) => handleRecipientChange(recipient.id, 'phone', e.target.value)}
                    className="form-control"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'var(--text-secondary)',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Role
                  </label>
                  <select
                    value={recipient.role}
                    onChange={(e) => handleRecipientChange(recipient.id, 'role', e.target.value)}
                    className="form-control"
                  >
                    <option value="Signer">Signer</option>
                    <option value="Approver">Approver</option>
                    <option value="Witness">Witness</option>
                    <option value="CC">CC (Copy)</option>
                  </select>
                </div>
              </div>

              {recipient.status === 'Completed' && recipient.signedAt && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#D1FAE5',
                  border: '1px solid #059669',
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '12px'
                }}>
                  <CheckCircle size={16} style={{ color: '#059669' }} />
                  <span style={{ fontSize: '14px', color: '#059669', fontFamily: 'var(--font-family)' }}>
                    Signed on {new Date(recipient.signedAt).toLocaleDateString()} at {new Date(recipient.signedAt).toLocaleTimeString()}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <h3 style={{ 
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-family)',
        fontSize: '18px',
        fontWeight: '600',
        marginBottom: '0'
      }}>
        Signature Configuration
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Signature Method
          </label>
          <select
            value={formData.signatureMethod}
            onChange={(e) => handleChange('signatureMethod', e.target.value)}
            className="form-control"
          >
            <option value="Electronic Signature">Electronic Signature</option>
            <option value="Digital Signature">Digital Signature</option>
            <option value="Advanced Electronic Signature">Advanced Electronic Signature</option>
          </select>
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Authentication Method
          </label>
          <select
            value={formData.authenticationMethod}
            onChange={(e) => handleChange('authenticationMethod', e.target.value)}
            className="form-control"
          >
            <option value="Email Verification">Email Verification</option>
            <option value="Email + SMS">Email + SMS</option>
            <option value="Email + Phone">Email + Phone</option>
            <option value="Email + SMS + ID Verification">Email + SMS + ID Verification</option>
          </select>
        </div>
      </div>

      <div style={{
        border: '1px solid var(--border-color)',
        borderRadius: '0',
        padding: '20px',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <input
            type="checkbox"
            checked={formData.reminderEnabled}
            onChange={(e) => handleChange('reminderEnabled', e.target.checked)}
            style={{ width: '18px', height: '18px' }}
          />
          <label style={{ 
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            margin: '0'
          }}>
            Enable Automatic Reminders
          </label>
        </div>

        {formData.reminderEnabled && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginLeft: '30px' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontSize: '12px',
                fontWeight: '500',
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-family)'
              }}>
                Reminder Frequency
              </label>
              <select
                value={formData.reminderFrequency}
                onChange={(e) => handleChange('reminderFrequency', e.target.value)}
                className="form-control"
              >
                <option value="daily">Daily</option>
                <option value="every-2-days">Every 2 Days</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontSize: '12px',
                fontWeight: '500',
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-family)'
              }}>
                Maximum Reminders
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={formData.maxReminders}
                onChange={(e) => handleChange('maxReminders', parseInt(e.target.value))}
                className="form-control"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h3 style={{ 
        marginBottom: '24px', 
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-family)',
        fontSize: '18px',
        fontWeight: '600'
      }}>
        Signature Progress
      </h3>

      {/* Overall Progress */}
      <div style={{
        border: '1px solid var(--border-color)',
        borderRadius: '0',
        padding: '24px',
        marginBottom: '24px',
        backgroundColor: 'white'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)'
          }}>
            Overall Progress
          </span>
          <span style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#4678be',
            fontFamily: 'var(--font-family)'
          }}>
            {document?.completionPercentage || 0}%
          </span>
        </div>
        <div style={{
          width: '100%',
          height: '12px',
          backgroundColor: '#e5e7eb',
          borderRadius: '0',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${document?.completionPercentage || 0}%`,
            height: '100%',
            backgroundColor: '#4678be',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          <span>{formData.recipients.filter(r => r.status === 'Completed').length} of {formData.recipients.length} signed</span>
          <span>Status: {document?.status || 'Pending Signature'}</span>
        </div>
      </div>

      {/* Recipients Progress */}
      <div>
        <h4 style={{
          marginBottom: '16px',
          fontSize: '16px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          fontFamily: 'var(--font-family)'
        }}>
          Recipient Status
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {formData.recipients.map((recipient) => (
            <div
              key={recipient.id}
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: '0',
                padding: '16px',
                backgroundColor: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: recipient.status === 'Completed' ? '#059669' : '#F59E0B',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)'
                  }}>
                    {recipient.order}
                  </span>
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'var(--text-primary)',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {recipient.name || 'Unnamed Recipient'}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {recipient.email} • {recipient.role}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span className={`badge ${
                  recipient.status === 'Completed' ? 'badge-success' :
                  recipient.status === 'In Progress' ? 'badge-primary' :
                  'badge-warning'
                }`}>
                  {recipient.status}
                </span>
                {recipient.status !== 'Completed' && (
                  <button
                    type="button"
                    onClick={() => handleSendReminder(recipient.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily: 'var(--font-family)'
                    }}
                  >
                    <Bell size={14} />
                    Send Reminder
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title={formData.documentName || 'E-Signature Document'}
        description={`Manage e-signature document: ${formData.contractId}`}
        actions={headerActions}
      />
      <div style={{ padding: '24px' }}>
        {/* Draft Notice Banner */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 24px',
          backgroundColor: 'var(--accent-color)',
          border: '1px solid var(--border-color)',
          borderRadius: '0',
          fontSize: '14px',
          color: 'var(--text-primary)',
          marginBottom: '24px',
          fontFamily: 'var(--font-family)'
        }}>
          <AlertCircle size={18} style={{ marginRight: '12px', color: 'var(--gold-primary)' }} />
          <div>
            <strong>E-Signature Setup:</strong> Configure document details, recipients, and signature settings before sending for signatures.
          </div>
        </div>

        <div style={{
          background: 'white',
          border: '1px solid var(--border-color)',
          borderRadius: '0',
          boxShadow: 'var(--shadow-sm)',
          overflow: 'hidden'
        }}>
          {/* Multi-step Header */}
          <FormStepper
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />

          <div style={{ padding: '32px' }}>
            <form onSubmit={currentStep === STEPS.length - 1 ? handleSave : handleNext}>
              {/* Animated Current Step */}
              <div key={currentStep} style={{ animation: 'fadeIn 0.25s ease-in' }}>
                {stepRenderers[currentStep]()}
              </div>

              {/* Navigation Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '32px 0 0 0',
                borderTop: '1px solid var(--border-color)',
                marginTop: '40px'
              }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn btn-secondary"
                    style={{ minWidth: '100px', borderRadius: '0', padding: '12px 24px' }}
                  >
                    <X size={16} />
                    Cancel
                  </button>
                  {currentStep > 0 && (
                    <button
                      type="button"
                      onClick={handleBack}
                      className="btn btn-secondary"
                      style={{
                        minWidth: '100px',
                        borderRadius: '0',
                        padding: '12px 24px',
                        border: '1px solid var(--gold-primary)',
                        color: 'var(--gold-primary)'
                      }}
                    >
                      <ChevronLeft size={16} />
                      Back
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  {/* Step dots */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {STEPS.map((_, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: idx === currentStep ? '20px' : '8px',
                          height: '8px',
                          borderRadius: '4px',
                          background: idx < currentStep ? 'var(--gold-bright)' : idx === currentStep ? 'var(--gold-primary)' : 'var(--divider-color)',
                          transition: 'all 0.3s ease',
                        }}
                      />
                    ))}
                  </div>

                  {currentStep < STEPS.length - 1 ? (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{
                        minWidth: '130px',
                        borderRadius: '0',
                        padding: '12px 28px',
                        backgroundColor: 'var(--gold-primary)',
                        border: 'none'
                      }}
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowSendModal(true)}
                      disabled={sending}
                      className="btn btn-success"
                      style={{
                        minWidth: '160px',
                        borderRadius: '0',
                        padding: '12px 28px',
                        backgroundColor: document?.status === 'Completed' ? 'var(--text-muted)' : '#059669',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: document?.status === 'Completed' ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Send size={16} />
                      {document?.status === 'Completed' ? 'Already Signed' : 'Send for Signature'}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Send for Signature Modal */}
      <ConfirmationModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        onConfirm={handleSendForSignature}
        title="Send for Signature"
        message={`Are you sure you want to send "${formData.documentName}" for signature to ${formData.recipients.length} recipient(s)?`}
        confirmText={sending ? 'Sending...' : 'Send'}
        cancelText="Cancel"
        type="primary"
        isLoading={sending}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Document"
        message={`Are you sure you want to delete "${formData.documentName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Global Fade In Animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default ESignatureEdit;

